/**
 * LinkedIn Profile Summary Generator
 *
 * Creates optimized LinkedIn content for career transitions:
 * - Headline variations (3 options)
 * - Summary/About section
 * - Experience reframing suggestions
 * - Skills keywords for ATS
 */

import { CareerPath } from './careerPaths';

export interface LinkedInProfile {
  headlines: HeadlineOption[];
  summary: string;
  aboutSections: AboutSection[];
  experienceReframes: ExperienceReframe[];
  skillKeywords: SkillKeyword[];
  tips: ProfileTip[];
}

export interface HeadlineOption {
  text: string;
  style: 'aspirational' | 'hybrid' | 'achievement';
  description: string;
}

export interface AboutSection {
  title: string;
  content: string;
}

export interface ExperienceReframe {
  scenario: string;
  original: string;
  reframed: string;
  whyItWorks: string;
}

export interface SkillKeyword {
  keyword: string;
  category: 'core' | 'technical' | 'soft' | 'industry';
  priority: 'must-have' | 'recommended' | 'nice-to-have';
}

export interface ProfileTip {
  section: string;
  tip: string;
  impact: 'high' | 'medium';
}

// Career-specific headline templates
const CAREER_HEADLINES: Record<string, HeadlineOption[]> = {
  web_developer: [
    {
      text: "Aspiring Web Developer | Building with React, JavaScript & Modern CSS | Open to Entry-Level Opportunities",
      style: 'aspirational',
      description: "Clear about your goal, shows tech stack, signals availability"
    },
    {
      text: "[Current Role] Transitioning to Web Development | Self-Taught in React & Node.js | Portfolio: yoursite.com",
      style: 'hybrid',
      description: "Bridges current experience with new direction, adds credibility"
    },
    {
      text: "Career Changer | Built 5+ Web Projects | Seeking Junior Developer Role | JavaScript, React, CSS",
      style: 'achievement',
      description: "Leads with proof of work, not just aspiration"
    },
  ],
  customer_success_specialist: [
    {
      text: "Customer Success Professional | Helping Clients Achieve Their Goals | SaaS & Tech Enthusiast",
      style: 'aspirational',
      description: "Customer-focused headline that speaks to value delivery"
    },
    {
      text: "[Current Role] → Customer Success | 5+ Years Client Management | HubSpot Certified",
      style: 'hybrid',
      description: "Shows transition path with relevant certifications"
    },
    {
      text: "Relationship Builder | 95% Client Retention | Seeking Customer Success Role in SaaS",
      style: 'achievement',
      description: "Leads with metrics that matter in CS"
    },
  ],
  project_manager: [
    {
      text: "Project Manager | PMP/Agile Certified | Delivering Results Through Cross-Functional Leadership",
      style: 'aspirational',
      description: "Certification-forward for credibility"
    },
    {
      text: "Operations Professional → Project Management | Google PM Certified | SCRUM Practitioner",
      style: 'hybrid',
      description: "Shows transition with relevant credentials"
    },
    {
      text: "Project Leader | $2M+ Budgets Managed | 15+ Projects Delivered On Time | Seeking PM Role",
      style: 'achievement',
      description: "Quantified achievements demonstrate capability"
    },
  ],
  electrician: [
    {
      text: "Electrical Apprentice | IBEW Local [XXX] | Commercial & Residential | OSHA 10 Certified",
      style: 'aspirational',
      description: "Shows union affiliation and safety certification"
    },
    {
      text: "Construction → Electrical Trade | Pre-Apprenticeship Complete | Eager to Learn & Grow",
      style: 'hybrid',
      description: "Honest about entry-level while showing initiative"
    },
    {
      text: "Future Journeyman Electrician | Hands-On Learner | Available for Apprenticeship Opportunities",
      style: 'achievement',
      description: "Long-term vision with immediate availability"
    },
  ],
  graphic_designer: [
    {
      text: "Graphic Designer | Branding, UI/UX & Print | Creating Visual Stories That Convert",
      style: 'aspirational',
      description: "Covers specialization range with value prop"
    },
    {
      text: "Marketing → Design | Self-Taught in Figma & Adobe CC | Portfolio: yoursite.com",
      style: 'hybrid',
      description: "Career transition with proof of tools mastery"
    },
    {
      text: "Visual Designer | 20+ Projects Completed | Freelance Available | Figma, Illustrator, Photoshop",
      style: 'achievement',
      description: "Proves capability through portfolio volume"
    },
  ],
  medical_assistant: [
    {
      text: "Certified Medical Assistant | Patient-Focused Care | CPR/BLS Certified | Eager to Contribute",
      style: 'aspirational',
      description: "Shows certification and patient orientation"
    },
    {
      text: "Healthcare Professional | CMA Certification In Progress | Phlebotomy & EHR Experience",
      style: 'hybrid',
      description: "Honest about progress while showing skills"
    },
    {
      text: "Medical Assistant | Externship Complete | 100+ Patient Interactions | Seeking Clinical Role",
      style: 'achievement',
      description: "Quantifies experience from training"
    },
  ],
  personal_trainer: [
    {
      text: "NASM Certified Personal Trainer | Helping Clients Transform | Weight Loss & Strength Specialist",
      style: 'aspirational',
      description: "Certification + specialization + value"
    },
    {
      text: "Fitness Enthusiast → Certified Trainer | 50+ Transformation Photos | Online & In-Person",
      style: 'hybrid',
      description: "Shows journey and proof of results"
    },
    {
      text: "Personal Trainer | 30+ Clients Transformed | Nutrition & Training Plans | Accepting New Clients",
      style: 'achievement',
      description: "Metrics + availability"
    },
  ],
};

// Career-specific summary templates
const CAREER_SUMMARIES: Record<string, string> = {
  web_developer: `I'm a passionate web developer building my skills in modern front-end and full-stack development. After [X years/background in Y], I discovered my love for creating intuitive, responsive web experiences.

What drives me:
- Turning complex problems into elegant, user-friendly solutions
- Continuous learning (currently deep-diving into [React/Node/specific tech])
- Building projects that make a real difference

I've built [X] projects including [brief project descriptions], all live and deployed. I'm especially interested in [specific area: e-commerce, accessibility, data visualization, etc.].

I'm actively seeking entry-level web development opportunities where I can contribute while growing with a team. I bring [transferable skill from past experience] and a genuine enthusiasm for the craft.

Let's connect if you're hiring junior developers or just want to chat about web dev!`,

  customer_success_specialist: `I help customers achieve their goals while driving business outcomes. With [X years] of experience in [previous field], I've developed a deep understanding of what it takes to build lasting client relationships.

What I bring to Customer Success:
- Natural ability to understand customer needs and translate them into solutions
- Experience managing [X number] client relationships simultaneously
- Passion for technology and quick to learn new SaaS platforms

I'm particularly drawn to customer success because I believe the best business outcomes happen when customers genuinely succeed. I'm not just looking to retain accounts—I want to be a true partner in my clients' success.

Currently [studying for CRM certification/completing training/etc.], I'm ready to bring my relationship-building expertise to a customer success role in tech.

Open to connecting with CS professionals and hiring managers in the SaaS space.`,

  project_manager: `Project manager with a passion for turning chaos into delivery. I bring [X years] of experience leading cross-functional initiatives, driving alignment, and ensuring projects hit their goals.

My approach:
- Start with the 'why' - understanding business objectives before diving into execution
- Build relationships first - projects are delivered by people, not plans
- Embrace agile principles - iterating based on feedback rather than following rigid roadmaps

I've managed projects with budgets up to [$X] and teams of [X] people, consistently delivering on time and within scope. My background in [previous field] gives me unique insight into [specific domain].

Certified in [PMP/Google PM/Scrum], I combine formal methodology with practical experience. I'm seeking opportunities where I can apply my skills to meaningful work.

Let's connect if you're looking for a PM who delivers results while keeping teams engaged.`,

  electrician: `Aspiring electrician committed to learning the trade and building a skilled career. I'm drawn to electrical work for its combination of hands-on problem-solving, technical knowledge, and the opportunity to work on projects that power people's lives.

Why electrical:
- I've always been the person who figures out how things work
- I want a career where I'm building real skills, not just doing tasks
- The demand for skilled electricians means job security and growth

I've completed [pre-apprenticeship training/OSHA certification/etc.] and I'm eager to learn from experienced journeymen. I understand this is a profession where you earn your skills through years of dedicated work, and I'm ready for that journey.

Currently seeking apprenticeship opportunities in [commercial/residential/industrial] electrical work. I'm reliable, safety-conscious, and ready to work hard.

Open to connecting with electricians, contractors, and anyone in the trades.`,

  graphic_designer: `Designer who believes great design should work as hard as it looks. I create visual solutions for brands, products, and digital experiences—always with the end user in mind.

My design philosophy:
- Form follows function - beautiful design that doesn't convert isn't doing its job
- Listen before you design - understanding the problem is 80% of the solution
- Iterate relentlessly - first ideas are starting points, not destinations

I work across [branding/UI/print/digital] using [Figma/Adobe Creative Suite/other tools]. My portfolio at [URL] showcases [X] projects including [brief highlights].

Coming from [previous background], I bring a unique perspective on [how that experience enhances your design work]. I'm actively seeking opportunities in [specific area: startup branding, product design, agency work].

Love connecting with fellow designers and creative teams building impactful work.`,

  medical_assistant: `Certified Medical Assistant committed to providing compassionate, efficient patient care. I'm drawn to healthcare because I want to make a direct, positive impact on people's lives during some of their most vulnerable moments.

My clinical skills include:
- Patient intake and vital signs
- Phlebotomy and specimen collection
- EHR documentation and scheduling
- Patient education and follow-up

I completed my externship at [facility type] where I gained hands-on experience with [X] patients across [specialties]. I'm CPR/BLS certified and trained in [specific procedures].

What sets me apart is my [specific quality from past experience: customer service background, attention to detail, calm under pressure, etc.]. Healthcare is where I belong, and I'm ready to contribute to a team that puts patients first.

Seeking clinical medical assistant positions in [specialty preferences]. Let's connect!`,

  personal_trainer: `Certified personal trainer passionate about helping people transform their health and confidence. I believe fitness should be sustainable, enjoyable, and tailored to each person's unique goals and lifestyle.

My training approach:
- Start where you are - no judgment, just progress
- Build habits, not just workouts - sustainable change over quick fixes
- Celebrate every win - mindset matters as much as movement

I specialize in [weight loss/strength training/sports performance/senior fitness] and work with clients both in-person and online. My [NASM/ACE/ISSA] certification provides the foundation, but my results speak louder.

[X] clients have achieved [specific outcomes: weight loss totals, strength gains, completed events]. I believe in practicing what I preach—fitness is my lifestyle, not just my job.

Currently accepting new clients and open to gym or studio opportunities. Let's connect if you're ready to start your transformation or if you're hiring trainers!`,
};

// Career-specific skill keywords
const CAREER_SKILLS: Record<string, SkillKeyword[]> = {
  web_developer: [
    { keyword: 'JavaScript', category: 'technical', priority: 'must-have' },
    { keyword: 'React', category: 'technical', priority: 'must-have' },
    { keyword: 'HTML5', category: 'technical', priority: 'must-have' },
    { keyword: 'CSS3', category: 'technical', priority: 'must-have' },
    { keyword: 'Node.js', category: 'technical', priority: 'recommended' },
    { keyword: 'Git', category: 'technical', priority: 'must-have' },
    { keyword: 'TypeScript', category: 'technical', priority: 'recommended' },
    { keyword: 'REST APIs', category: 'technical', priority: 'recommended' },
    { keyword: 'Responsive Design', category: 'core', priority: 'must-have' },
    { keyword: 'Problem Solving', category: 'soft', priority: 'must-have' },
    { keyword: 'Agile', category: 'industry', priority: 'recommended' },
    { keyword: 'Web Accessibility', category: 'core', priority: 'nice-to-have' },
  ],
  customer_success_specialist: [
    { keyword: 'Customer Success', category: 'core', priority: 'must-have' },
    { keyword: 'Client Relationship Management', category: 'core', priority: 'must-have' },
    { keyword: 'SaaS', category: 'industry', priority: 'must-have' },
    { keyword: 'CRM Software', category: 'technical', priority: 'must-have' },
    { keyword: 'Salesforce', category: 'technical', priority: 'recommended' },
    { keyword: 'HubSpot', category: 'technical', priority: 'recommended' },
    { keyword: 'Customer Retention', category: 'core', priority: 'must-have' },
    { keyword: 'Onboarding', category: 'core', priority: 'must-have' },
    { keyword: 'Account Management', category: 'core', priority: 'recommended' },
    { keyword: 'Data Analysis', category: 'technical', priority: 'recommended' },
    { keyword: 'Communication', category: 'soft', priority: 'must-have' },
    { keyword: 'Problem Solving', category: 'soft', priority: 'must-have' },
  ],
  project_manager: [
    { keyword: 'Project Management', category: 'core', priority: 'must-have' },
    { keyword: 'Agile', category: 'industry', priority: 'must-have' },
    { keyword: 'Scrum', category: 'industry', priority: 'must-have' },
    { keyword: 'PMP', category: 'industry', priority: 'recommended' },
    { keyword: 'Stakeholder Management', category: 'core', priority: 'must-have' },
    { keyword: 'Risk Management', category: 'core', priority: 'must-have' },
    { keyword: 'Jira', category: 'technical', priority: 'recommended' },
    { keyword: 'Asana', category: 'technical', priority: 'nice-to-have' },
    { keyword: 'Budget Management', category: 'core', priority: 'recommended' },
    { keyword: 'Cross-functional Leadership', category: 'soft', priority: 'must-have' },
    { keyword: 'Communication', category: 'soft', priority: 'must-have' },
    { keyword: 'Strategic Planning', category: 'core', priority: 'recommended' },
  ],
  electrician: [
    { keyword: 'Electrical Installation', category: 'core', priority: 'must-have' },
    { keyword: 'National Electrical Code', category: 'industry', priority: 'must-have' },
    { keyword: 'Blueprint Reading', category: 'technical', priority: 'must-have' },
    { keyword: 'OSHA Safety', category: 'industry', priority: 'must-have' },
    { keyword: 'Troubleshooting', category: 'core', priority: 'must-have' },
    { keyword: 'Wiring', category: 'technical', priority: 'must-have' },
    { keyword: 'Conduit Bending', category: 'technical', priority: 'recommended' },
    { keyword: 'Power Distribution', category: 'technical', priority: 'recommended' },
    { keyword: 'Safety Compliance', category: 'core', priority: 'must-have' },
    { keyword: 'Hand Tools', category: 'technical', priority: 'must-have' },
    { keyword: 'Problem Solving', category: 'soft', priority: 'must-have' },
    { keyword: 'Teamwork', category: 'soft', priority: 'recommended' },
  ],
  graphic_designer: [
    { keyword: 'Graphic Design', category: 'core', priority: 'must-have' },
    { keyword: 'Adobe Creative Suite', category: 'technical', priority: 'must-have' },
    { keyword: 'Figma', category: 'technical', priority: 'must-have' },
    { keyword: 'UI/UX Design', category: 'core', priority: 'recommended' },
    { keyword: 'Branding', category: 'core', priority: 'must-have' },
    { keyword: 'Typography', category: 'core', priority: 'must-have' },
    { keyword: 'Color Theory', category: 'core', priority: 'must-have' },
    { keyword: 'Layout Design', category: 'core', priority: 'must-have' },
    { keyword: 'Illustration', category: 'technical', priority: 'nice-to-have' },
    { keyword: 'Print Design', category: 'core', priority: 'recommended' },
    { keyword: 'Visual Communication', category: 'soft', priority: 'must-have' },
    { keyword: 'Creativity', category: 'soft', priority: 'must-have' },
  ],
  medical_assistant: [
    { keyword: 'Medical Assistant', category: 'core', priority: 'must-have' },
    { keyword: 'Patient Care', category: 'core', priority: 'must-have' },
    { keyword: 'Vital Signs', category: 'technical', priority: 'must-have' },
    { keyword: 'Phlebotomy', category: 'technical', priority: 'must-have' },
    { keyword: 'EHR', category: 'technical', priority: 'must-have' },
    { keyword: 'Medical Terminology', category: 'industry', priority: 'must-have' },
    { keyword: 'CPR/BLS Certified', category: 'industry', priority: 'must-have' },
    { keyword: 'HIPAA', category: 'industry', priority: 'must-have' },
    { keyword: 'Patient Scheduling', category: 'technical', priority: 'recommended' },
    { keyword: 'Clinical Documentation', category: 'core', priority: 'must-have' },
    { keyword: 'Compassionate Care', category: 'soft', priority: 'must-have' },
    { keyword: 'Attention to Detail', category: 'soft', priority: 'must-have' },
  ],
  personal_trainer: [
    { keyword: 'Personal Training', category: 'core', priority: 'must-have' },
    { keyword: 'NASM Certified', category: 'industry', priority: 'must-have' },
    { keyword: 'Fitness Assessment', category: 'core', priority: 'must-have' },
    { keyword: 'Exercise Programming', category: 'core', priority: 'must-have' },
    { keyword: 'Nutrition Coaching', category: 'core', priority: 'recommended' },
    { keyword: 'Weight Loss', category: 'core', priority: 'recommended' },
    { keyword: 'Strength Training', category: 'technical', priority: 'must-have' },
    { keyword: 'CPR/AED Certified', category: 'industry', priority: 'must-have' },
    { keyword: 'Client Motivation', category: 'soft', priority: 'must-have' },
    { keyword: 'Goal Setting', category: 'soft', priority: 'must-have' },
    { keyword: 'Communication', category: 'soft', priority: 'must-have' },
    { keyword: 'Online Training', category: 'technical', priority: 'nice-to-have' },
  ],
};

// Experience reframe examples
const EXPERIENCE_REFRAMES: Record<string, ExperienceReframe[]> = {
  web_developer: [
    {
      scenario: "Retail/Customer Service → Web Dev",
      original: "Cashier at Target - Processed transactions, helped customers",
      reframed: "Developed problem-solving skills handling 100+ daily customer interactions; gained attention to detail managing high-accuracy transactions",
      whyItWorks: "Connects soft skills to developer requirements"
    },
    {
      scenario: "Office Admin → Web Dev",
      original: "Administrative Assistant - Data entry, scheduling, filing",
      reframed: "Built strong attention to detail through data accuracy work; developed systematic approach to organizing complex information - skills directly applicable to writing clean, organized code",
      whyItWorks: "Frames admin work as proto-developer skills"
    },
  ],
  customer_success_specialist: [
    {
      scenario: "Retail → Customer Success",
      original: "Sales Associate - Helped customers find products",
      reframed: "Drove customer satisfaction through consultative approach, understanding needs before recommending solutions; achieved top 10% customer satisfaction scores",
      whyItWorks: "Emphasizes the consultative, relationship-building aspect"
    },
    {
      scenario: "Restaurant/Hospitality → CS",
      original: "Server at restaurant - Took orders, delivered food",
      reframed: "Managed multiple client relationships simultaneously while maintaining high satisfaction; developed ability to anticipate needs and proactively address concerns",
      whyItWorks: "Frames hospitality as multi-account management"
    },
  ],
  project_manager: [
    {
      scenario: "Team Lead → PM",
      original: "Team Lead - Supervised 5 employees",
      reframed: "Led cross-functional team of 5, coordinating workflows, managing priorities, and ensuring on-time delivery of team objectives",
      whyItWorks: "Uses PM language for leadership experience"
    },
    {
      scenario: "Event Planning → PM",
      original: "Event Coordinator - Planned company events",
      reframed: "Managed end-to-end project delivery for 20+ events annually, coordinating vendors, budgets, timelines, and stakeholder expectations",
      whyItWorks: "Events ARE projects - frame them that way"
    },
  ],
};

// Profile tips
const UNIVERSAL_TIPS: ProfileTip[] = [
  {
    section: 'Photo',
    tip: 'Use a professional headshot with good lighting. Profiles with photos get 21x more views.',
    impact: 'high'
  },
  {
    section: 'Headline',
    tip: 'Include your target role title - recruiters search by job title keywords.',
    impact: 'high'
  },
  {
    section: 'About',
    tip: 'First 3 lines show without clicking "See more" - front-load keywords and value prop.',
    impact: 'high'
  },
  {
    section: 'Experience',
    tip: 'Use quantified achievements (numbers, percentages, dollar amounts) whenever possible.',
    impact: 'high'
  },
  {
    section: 'Skills',
    tip: 'Add 10+ relevant skills and ask connections to endorse your top 3.',
    impact: 'medium'
  },
  {
    section: 'Activity',
    tip: 'Engage with content in your target industry. Comment thoughtfully on posts from target companies.',
    impact: 'medium'
  },
  {
    section: 'Settings',
    tip: 'Enable "Open to Work" (recruiters only) to increase inbound opportunities by 2x.',
    impact: 'high'
  },
  {
    section: 'URL',
    tip: 'Customize your LinkedIn URL to linkedin.com/in/yourname for cleaner resume appearance.',
    impact: 'medium'
  },
];

/**
 * Generate a complete LinkedIn profile optimization package
 */
export function generateLinkedInProfile(
  career: CareerPath,
  userSegment: string | null = null,
  currentBackground: string | null = null
): LinkedInProfile {
  // Get career-specific content or generate defaults
  const headlines = CAREER_HEADLINES[career.id] || generateDefaultHeadlines(career);
  const summary = CAREER_SUMMARIES[career.id] || generateDefaultSummary(career);
  const skills = CAREER_SKILLS[career.id] || generateDefaultSkills(career);
  const reframes = EXPERIENCE_REFRAMES[career.id] || [];

  // Build about sections
  const aboutSections: AboutSection[] = [
    {
      title: "Opening Hook (First 3 Lines)",
      content: getOpeningHook(career, userSegment)
    },
    {
      title: "Full Summary Template",
      content: summary
    },
    {
      title: "Call to Action",
      content: getCTA(career)
    },
  ];

  // Add segment-specific tips
  const tips = [...UNIVERSAL_TIPS];
  if (userSegment === 'veteran') {
    tips.push({
      section: 'Experience',
      tip: 'Translate military roles to civilian terms. Use tools like O*NET Military Crosswalk.',
      impact: 'high'
    });
  }
  if (userSegment === 'returning') {
    tips.push({
      section: 'About',
      tip: 'Address career gap positively: "After [reason], I\'m returning to the workforce with [skill/energy/perspective]."',
      impact: 'high'
    });
  }

  return {
    headlines,
    summary,
    aboutSections,
    experienceReframes: reframes,
    skillKeywords: skills,
    tips,
  };
}

function generateDefaultHeadlines(career: CareerPath): HeadlineOption[] {
  return [
    {
      text: `Aspiring ${career.title} | [Your Top Skill] | Open to Opportunities`,
      style: 'aspirational',
      description: "Simple, clear, searchable"
    },
    {
      text: `[Current Role] → ${career.title} | Skills: [List 2-3] | Portfolio/Proof`,
      style: 'hybrid',
      description: "Shows career direction"
    },
    {
      text: `Future ${career.title} | [X Achievement/Credential] | Seeking [Entry/Junior] Role`,
      style: 'achievement',
      description: "Lead with proof"
    },
  ];
}

function generateDefaultSummary(career: CareerPath): string {
  return `I'm transitioning into ${career.title}, bringing [X years] of experience from [your background] and a genuine passion for ${career.category.toLowerCase()}.

What drives me:
- [Key motivation related to this career]
- [Relevant skill or project you've built]
- [What you're excited to learn or contribute]

I'm actively seeking entry-level ${career.title} opportunities where I can contribute while growing. I bring [transferable skill] and enthusiasm for the work.

Let's connect if you're hiring or just want to chat about ${career.category.toLowerCase()}!`;
}

function generateDefaultSkills(career: CareerPath): SkillKeyword[] {
  const skills: SkillKeyword[] = career.requiredSkills.map(skill => ({
    keyword: skill,
    category: 'core' as const,
    priority: 'must-have' as const
  }));

  // Add some universal soft skills
  skills.push(
    { keyword: 'Problem Solving', category: 'soft', priority: 'must-have' },
    { keyword: 'Communication', category: 'soft', priority: 'must-have' },
    { keyword: 'Teamwork', category: 'soft', priority: 'recommended' }
  );

  return skills;
}

function getOpeningHook(career: CareerPath, segment: string | null): string {
  if (segment === 'veteran') {
    return `Military veteran transitioning to ${career.title}. I bring [discipline/leadership/problem-solving] from [X years] of service, now channeled into building a career in ${career.category.toLowerCase()}.`;
  }
  if (segment === 'returning') {
    return `Returning to the workforce with renewed focus on ${career.title}. [Brief context]. Ready to bring my [skills/experience] to a team where I can grow and contribute.`;
  }
  if (segment === 'career-changer') {
    return `${career.title} in training. After [X years] in [previous field], I discovered my passion for ${career.category.toLowerCase()}. Now I'm all in on building this new chapter.`;
  }

  return `I help [target audience] by [key value you deliver as a ${career.title}]. Passionate about ${career.category.toLowerCase()} and always learning.`;
}

function getCTA(career: CareerPath): string {
  return `I'm actively seeking ${career.title} opportunities and love connecting with others in the field. Feel free to reach out if:
- You're hiring for ${career.title} roles
- You work in ${career.category.toLowerCase()} and want to connect
- You have advice for someone breaking into this field

Best way to reach me: [LinkedIn message / email@example.com]`;
}

/**
 * Get keyword density score for a profile text
 */
export function analyzeKeywordCoverage(
  profileText: string,
  career: CareerPath
): { score: number; missing: string[]; included: string[] } {
  const skills = CAREER_SKILLS[career.id] || generateDefaultSkills(career);
  const mustHaveSkills = skills.filter(s => s.priority === 'must-have');

  const textLower = profileText.toLowerCase();
  const included: string[] = [];
  const missing: string[] = [];

  for (const skill of mustHaveSkills) {
    if (textLower.includes(skill.keyword.toLowerCase())) {
      included.push(skill.keyword);
    } else {
      missing.push(skill.keyword);
    }
  }

  const score = Math.round((included.length / mustHaveSkills.length) * 100);

  return { score, missing, included };
}
